import { cloneDeep, isEmpty, zip } from "lodash";
import type {
  Schema,
  SchemaField,
  InferSchemaOptions,
} from "@aqora-io/aqora-data-utils";
import {
  DeepPartial,
  FieldNamesMarkedBoolean,
  FieldValues,
} from "react-hook-form";
import {
  SimpleSchemaFieldKind,
  ScalarType,
  SimpleSchemaField,
  SimpleSchema,
} from "@/utils/data_utils_schema";

const apply_scalar = (
  field: SchemaField,
  scalar: ScalarType,
  options?: InferSchemaOptions,
): SchemaField => {
  const rest = {
    name: field.name,
    strategy: field.strategy,
    nullable: field.nullable,
    metadata: field.metadata,
  };
  switch (scalar) {
    case ScalarType.Null:
      return { data_type: "Null", ...rest };
    case ScalarType.Boolean:
      return { data_type: "Boolean", ...rest };
    case ScalarType.Integer:
      return { data_type: "Int64", ...rest };
    case ScalarType.Float:
      return { data_type: "Float64", ...rest };
    case ScalarType.Decimal:
      return { data_type: "Decimal128", precision: 5, scale: 2, ...rest };
    case ScalarType.Date:
      return { data_type: "Date32", ...rest };
    case ScalarType.Timestamp:
      return {
        data_type: "Timestamp",
        unit: "Millisecond",
        tz: "Utc",
        ...rest,
      };
    case ScalarType.Time:
      return { data_type: "Time32", unit: "Millisecond", ...rest };
    case ScalarType.Duration:
      return { data_type: "Duration", unit: "Millisecond", ...rest };
    case ScalarType.String:
      return {
        data_type: options?.string_as_small_utf8 ? "Utf8" : "LargeUtf8",
        ...rest,
      };
    case ScalarType.Binary:
      return {
        data_type: options?.bytes_as_small_binary ? "Binary" : "LargeBinary",
        ...rest,
      };
  }
};

export const apply_edit_field = (
  field: SchemaField,
  edit_field: SimpleSchemaField,
  options?: InferSchemaOptions,
): SchemaField => {
  if (field.name !== edit_field.name) {
    throw new Error("Field name mismatch");
  }
  field.nullable = edit_field.nullable;
  switch (edit_field.kind) {
    case SimpleSchemaFieldKind.Scalar:
      return apply_scalar(field, edit_field.scalar, options);
    case SimpleSchemaFieldKind.List:
      switch (field.data_type) {
        case "List":
        case "LargeList":
        case "FixedSizeList":
        case "ListView":
          field.field = apply_edit_field(
            field.field,
            edit_field.field,
            options,
          );
          return field;
        default:
          throw new Error("Field is not a list type");
      }
    case SimpleSchemaFieldKind.Struct:
      switch (field.data_type) {
        case "Struct":
          field.fields = zip<SchemaField, SimpleSchemaField>(
            field.fields,
            edit_field.fields,
          ).map(([field, edit_field]) => {
            if (!field || !edit_field) {
              throw new Error("Field length mismatch");
            }
            return apply_edit_field(field, edit_field, options);
          });
          return field;
        default:
          throw new Error("Field is not a struct type");
      }
    default:
      return field;
  }
};

type Dirty<T extends FieldValues> = DeepPartial<
  Readonly<FieldNamesMarkedBoolean<T>>
>;

export const get_overwrites = (
  schema: Schema,
  edit_schema: SimpleSchema,
  dirty_schema: Dirty<SimpleSchema>,
  infer_schema_options?: InferSchemaOptions,
) => {
  const overwrites = cloneDeep(infer_schema_options?.overwrites ?? {});
  const scopes: [
    string,
    SchemaField,
    SimpleSchemaField,
    Dirty<SimpleSchemaField>,
  ][] = [];
  for (const index in edit_schema.fields) {
    scopes.push([
      "",
      schema.fields[index],
      edit_schema.fields[index],
      (dirty_schema?.fields || [])[index] || {},
    ]);
  }
  while (scopes.length > 0) {
    const [path, schema_field, edit_field, dirty] = scopes.pop()!;
    if (isEmpty(dirty)) {
      continue;
    }
    if (dirty.nullable || ("scalar" in dirty && dirty.scalar)) {
      overwrites[`${path}${schema_field.name}`] = apply_edit_field(
        schema_field,
        edit_field,
        infer_schema_options,
      );
      continue;
    }
    switch (edit_field.kind) {
      case SimpleSchemaFieldKind.List:
        scopes.push([
          `${path}${edit_field.name}.`,
          (
            schema_field as {
              data_type: "List" | "LargeList" | "FixedSizeList" | "ListView";
              field: SchemaField;
            }
          ).field,
          edit_field.field,
          (dirty as { field?: Dirty<SimpleSchemaField> }).field || {},
        ]);
        break;
      case SimpleSchemaFieldKind.Struct:
        for (const index in edit_field.fields) {
          scopes.push([
            `${path}${edit_field.name}.`,
            (schema_field as { data_type: "Struct"; fields: SchemaField[] })
              .fields[index],
            edit_field.fields[index],
            ((dirty as { fields?: Dirty<SimpleSchemaField>[] }).fields || [])[
              index
            ],
          ]);
        }
    }
  }
  return isEmpty(overwrites) ? undefined : overwrites;
};
